/**
 * Customer Portal — Experiences, Memberships, Purchases pages.
 *
 * All three pull from /api/portal/customer/me — real DB data.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Calendar, Heart, Receipt, Download, ExternalLink, Loader2, PlayCircle,
  Search, CreditCard, CheckCircle2, FileText,
} from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const _money = (n, c = "USD") => {
  const sym = c === "USD" ? "$" : c === "EUR" ? "€" : c === "GBP" ? "£" : c === "INR" ? "₹" : "";
  return `${sym}${Number(n || 0).toFixed(2)}`;
};
const _date = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return ""; }
};
const _datetime = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

function usePortalData() {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/portal/customer/me").then((r) => setD(r.data)).catch(() => setD({}));
  }, []);
  return d;
}

// ---------------------------------------------------------------------------
// Experiences
// ---------------------------------------------------------------------------

export function CustomerExperiences() {
  const d = usePortalData();
  if (!d) return <div className="text-meta py-10 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading experiences…</div>;

  const now = Date.now();
  const allEvents = [
    ...(d.webinars || []).map((w) => ({ ...w, _kind: "Webinar", _at: w.at || w.starts_at })),
    ...(d.bookings || []).map((b) => ({ ...b, _kind: "Booking", _at: b.starts_at || b.at })),
  ];
  const upcoming = allEvents.filter((e) => { try { return e._at && new Date(e._at).getTime() > now; } catch { return false; } });
  const past = allEvents.filter((e) => { try { return !e._at || new Date(e._at).getTime() <= now; } catch { return true; } });

  return (
    <div data-testid="customer-experiences">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-ink inline-flex items-center gap-2"><Calendar className="h-6 w-6 text-ai" /> Experiences</h1>
        <p className="text-sm text-meta mt-1">Live sessions, replays, workshops, and 1:1 calls — all in one place.</p>
      </div>

      {allEvents.length === 0 ? (
        <div className="surface p-10 text-center" data-testid="experiences-empty">
          <Calendar className="h-8 w-8 text-zinc-300 mx-auto" />
          <p className="mt-3 font-display text-lg font-bold text-ink">No experiences yet</p>
          <p className="mt-1 text-sm text-meta max-w-md mx-auto">Webinars, workshops, and coaching sessions you sign up for will appear here with one-click access.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {upcoming.length > 0 && (
            <section data-testid="experiences-upcoming">
              <p className="text-xs uppercase tracking-wider text-meta font-semibold mb-3">Upcoming</p>
              <div className="space-y-3">
                {upcoming.map((e, i) => (
                  <div key={e.id || i} className="surface p-5 flex items-center justify-between gap-3" data-testid={`experience-upcoming-${i}`}>
                    <div className="min-w-0">
                      <span className="pill pill-ai text-[10px]">{e._kind}</span>
                      <p className="mt-1 font-semibold text-ink truncate">{e.title || e.product_name || "Session"}</p>
                      <p className="text-xs text-meta">{_datetime(e._at)}</p>
                    </div>
                    {e.url ? (
                      <a href={e.url} target="_blank" rel="noreferrer" className="text-xs font-semibold rounded-full ashra-gradient-bg text-white px-4 py-2 inline-flex items-center gap-1 shadow-sm hover:shadow-md focus-ring">
                        <PlayCircle className="h-3 w-3" /> Join
                      </a>
                    ) : (
                      <span className="pill pill-warning">link pending</span>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
          {past.length > 0 && (
            <section data-testid="experiences-past">
              <p className="text-xs uppercase tracking-wider text-meta font-semibold mb-3">Past · Replays</p>
              <div className="space-y-3">
                {past.map((e, i) => (
                  <div key={e.id || i} className="surface p-5 flex items-center justify-between gap-3" data-testid={`experience-past-${i}`}>
                    <div className="min-w-0">
                      <span className="pill pill-neutral text-[10px]">{e._kind}</span>
                      <p className="mt-1 font-semibold text-ink truncate">{e.title || e.product_name || "Session"}</p>
                      <p className="text-xs text-meta">{_datetime(e._at)}</p>
                    </div>
                    {e.replay_url ? (
                      <a href={e.replay_url} target="_blank" rel="noreferrer" className="text-xs font-semibold rounded-full bg-ink text-white px-4 py-2 inline-flex items-center gap-1">
                        <PlayCircle className="h-3 w-3" /> Watch replay
                      </a>
                    ) : (
                      <span className="text-xs text-meta">no replay</span>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Memberships
// ---------------------------------------------------------------------------

export function CustomerMemberships() {
  const d = usePortalData();
  if (!d) return <div className="text-meta py-10 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading memberships…</div>;

  const subs = [...(d.memberships || []), ...(d.subscriptions || [])];

  return (
    <div data-testid="customer-memberships">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-ink inline-flex items-center gap-2"><Heart className="h-6 w-6 text-rose-500" /> Memberships</h1>
        <p className="text-sm text-meta mt-1">Active recurring access and unlocked communities.</p>
      </div>

      {subs.length === 0 ? (
        <div className="surface p-10 text-center" data-testid="memberships-empty">
          <Heart className="h-8 w-8 text-zinc-300 mx-auto" />
          <p className="mt-3 font-display text-lg font-bold text-ink">No active memberships</p>
          <p className="mt-1 text-sm text-meta max-w-md mx-auto">When you join a creator's ongoing program, your benefits and renewal details appear here.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {subs.map((s, i) => {
            const active = (s.status || "active") === "active";
            const benefits = s.benefits || s.perks || [];
            const price = s.price ?? s.amount_usd ?? s.amount;
            const period = s.period || s.interval || "month";
            return (
              <div key={s.id || s.sub_id || i} className="surface p-6 relative overflow-hidden" data-testid={`membership-${i}`}>
                <div className="absolute -top-12 -right-12 h-32 w-32 rounded-full ashra-gradient-bg opacity-10 blur-2xl pointer-events-none" />
                <div className="flex items-center justify-between mb-3 relative">
                  <p className="font-display font-bold text-ink">{s.name || s.title || s.product_name || "Membership"}</p>
                  <span className={`pill ${active ? "pill-success" : "pill-neutral"}`}>{active ? "Active" : (s.status || "inactive")}</span>
                </div>
                {price !== undefined && (
                  <p className="font-display text-3xl font-extrabold text-ink relative">{_money(price, (s.currency || "USD").toUpperCase())}<span className="text-sm font-medium text-meta">/{period}</span></p>
                )}
                {s.next_charge_at && <p className="text-xs text-meta mt-2 relative">Renews {_date(s.next_charge_at)}</p>}
                {benefits.length > 0 && (
                  <ul className="mt-4 space-y-1.5 relative">
                    {benefits.slice(0, 5).map((b, j) => (
                      <li key={j} className="text-xs text-ink-soft inline-flex items-start gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" /> {b}</li>
                    ))}
                  </ul>
                )}
                {s.url && (
                  <a href={s.url} target="_blank" rel="noreferrer" className="mt-5 inline-block w-full text-center text-xs font-semibold rounded-full ashra-gradient-bg text-white px-4 py-2.5 shadow-sm">Open community</a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Purchases
// ---------------------------------------------------------------------------

export function CustomerPurchases() {
  const d = usePortalData();
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const ps = (d?.purchases) || [];
    const q = search.trim().toLowerCase();
    return q ? ps.filter((p) => (p.asset_title || "").toLowerCase().includes(q)) : ps;
  }, [d, search]);

  if (!d) return <div className="text-meta py-10 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading purchases…</div>;

  const purchases = d.purchases || [];

  const downloadReceipt = (p) => {
    // Minimal client-side receipt — premium feel, no backend dep.
    const lines = [
      "ASHRA AI — RECEIPT",
      "",
      `Order ref: ${p.id}`,
      `Date:      ${_date(p.created_at)}`,
      `Item:      ${p.asset_title}`,
      `Type:      ${(p.asset_type || "").replace("_", " ")}`,
      `Amount:    ${_money(p.amount_usd, (p.currency || "USD").toUpperCase())}`,
      `Status:    ${p.status || "completed"}`,
      "",
      "Thanks for your purchase!",
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `receipt-${p.id}.txt`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Receipt downloaded");
  };

  return (
    <div data-testid="customer-purchases">
      <div className="mb-6 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-ink inline-flex items-center gap-2"><Receipt className="h-6 w-6 text-ai" /> Purchases</h1>
          <p className="text-sm text-meta mt-1">Your invoices, receipts, and payment history.</p>
        </div>
        <div className="grid sm:grid-cols-3 gap-2 text-xs">
          <div className="rounded-xl border border-line bg-white px-3 py-2">
            <p className="text-meta">Orders</p>
            <p className="font-display font-extrabold text-ink">{d.total_orders || 0}</p>
          </div>
          <div className="rounded-xl border border-line bg-white px-3 py-2">
            <p className="text-meta">Total spent</p>
            <p className="font-display font-extrabold text-ink">{_money(d.total_spent, "USD")}</p>
          </div>
          <div className="rounded-xl border border-line bg-white px-3 py-2">
            <p className="text-meta">LTV with creators</p>
            <p className="font-display font-extrabold text-ink">{_money(d.ltv, "USD")}</p>
          </div>
        </div>
      </div>

      {purchases.length === 0 ? (
        <div className="surface p-10 text-center" data-testid="purchases-empty">
          <Receipt className="h-8 w-8 text-zinc-300 mx-auto" />
          <p className="mt-3 font-display text-lg font-bold text-ink">No purchases yet</p>
          <p className="mt-1 text-sm text-meta max-w-md mx-auto">When you buy from a creator, your receipts and downloadable products appear here.</p>
        </div>
      ) : (
        <>
          <div className="mb-4 relative max-w-sm" data-testid="purchases-search-wrap">
            <Search className="h-3.5 w-3.5 absolute left-3 top-2.5 text-meta" />
            <input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search purchases"
              data-testid="purchases-search"
              className="w-full rounded-full border border-line bg-white pl-9 pr-4 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          <div className="surface overflow-hidden" data-testid="purchases-table">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-meta uppercase tracking-wider font-semibold bg-zinc-50">
                <tr>
                  <th className="px-4 py-3">Item</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {filtered.map((p) => (
                  <tr key={p.id} className="hover:bg-zinc-50/60" data-testid={`purchase-${p.id}`}>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-ink">{p.asset_title || "Purchase"}</p>
                      <p className="text-[11px] text-meta-soft font-mono mt-0.5">{p.id}</p>
                    </td>
                    <td className="px-4 py-3 font-medium text-ink">{_money(p.amount_usd, (p.currency || "USD").toUpperCase())}</td>
                    <td className="px-4 py-3 text-meta">{_date(p.created_at)}</td>
                    <td className="px-4 py-3"><span className={`pill ${p.status === "completed" ? "pill-success" : "pill-neutral"}`}>{p.status || "completed"}</span></td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-2">
                        {(p.delivery?.redirect_url || p.delivery?.pdf_url || p.delivery?.drive_url || p.delivery?.video_url) && (
                          <a href={p.delivery.redirect_url || p.delivery.pdf_url || p.delivery.drive_url || p.delivery.video_url} target="_blank" rel="noreferrer"
                            className="text-xs font-semibold rounded-full ashra-gradient-bg text-white px-3 py-1.5 inline-flex items-center gap-1" data-testid={`purchase-open-${p.id}`}>
                            <ExternalLink className="h-3 w-3" /> Open
                          </a>
                        )}
                        <button onClick={() => downloadReceipt(p)} className="text-xs font-semibold rounded-full bg-zinc-100 text-ink-soft px-3 py-1.5 inline-flex items-center gap-1 hover:bg-zinc-200" data-testid={`purchase-receipt-${p.id}`}>
                          <Download className="h-3 w-3" /> Receipt
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
